import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Alert,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    Tune as OptionIcon
} from '@mui/icons-material';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import PricingPlansPanel from '../Componant/PricingPlansPanel';
import { getOptions, createOption, updateOption, deleteOption } from '../Utils/pricingApi';

const emptyOptionForm = { key: '', name: '', description: '', is_active: true };

export default function PricingManagementPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canManagePricing = isSuperAdmin || userPermissions.includes('applications.pricing.manage');
    // La route est ouverte à quiconque a "applications" (base), mais la grille tarifaire
    // elle-même est plus sensible : il faut en plus voir ou gérer explicitement les prix.
    const canViewPricing = canManagePricing || userPermissions.includes('applications.pricing.view');

    const [tabValue, setTabValue] = useState(0);

    const [options, setOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [optionsError, setOptionsError] = useState(null);
    const [optionDialogOpen, setOptionDialogOpen] = useState(false);
    const [editingOption, setEditingOption] = useState(null);
    const [optionForm, setOptionForm] = useState(emptyOptionForm);
    const [optionFormError, setOptionFormError] = useState('');
    const [expandedOption, setExpandedOption] = useState(false);

    const fetchOptions = useCallback(() => {
        if (!accessToken || !id) return;
        setLoadingOptions(true);
        getOptions(accessToken, id)
            .then(setOptions)
            .catch(() => setOptionsError('Impossible de charger les options'))
            .finally(() => setLoadingOptions(false));
    }, [accessToken, id]);

    useEffect(() => { fetchOptions(); }, [fetchOptions]);

    const openCreateOption = () => {
        setEditingOption(null);
        setOptionForm(emptyOptionForm);
        setOptionFormError('');
        setOptionDialogOpen(true);
    };

    const openEditOption = (option) => {
        setEditingOption(option);
        setOptionForm({
            key: option.key, name: option.name,
            description: option.description || '', is_active: option.is_active
        });
        setOptionFormError('');
        setOptionDialogOpen(true);
    };

    const handleSaveOption = () => {
        const req = editingOption
            ? updateOption(accessToken, editingOption.id, optionForm)
            : createOption(accessToken, { ...optionForm, application_id: id });

        req.then(() => { setOptionDialogOpen(false); fetchOptions(); })
           .catch(err => setOptionFormError(err.response?.data?.message || 'Erreur lors de l\'enregistrement'));
    };

    const handleDeleteOption = (option) => {
        if (!window.confirm(`Supprimer l'option "${option.name}" et ses plans tarifaires ?`)) return;
        deleteOption(accessToken, option.id).then(fetchOptions).catch(() => setOptionsError('Suppression impossible'));
    };

    if (!canViewPricing) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">Accès refusé</Typography>
                <Typography color="text.secondary">Vous n'avez pas la permission d'accéder à la tarification de cette application.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(`/applications/${id}`)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    Tarification
                </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    <Tab label="Tarification de l'application" />
                    <Tab label="Options du programme" />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <PricingPlansPanel accessToken={accessToken} priceableType="application" priceableId={id} applicationId={id} disabled={!canManagePricing} />
            )}

            {tabValue === 1 && (
                <Box>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        Les options sont des add-ons de cette application (ex: stockage supplémentaire, support premium).
                        Chacune peut avoir sa propre grille tarifaire, indépendante de celle de l'application.
                    </Alert>

                    {optionsError && <Alert severity="error" sx={{ mb: 2 }}>{optionsError}</Alert>}

                    {canManagePricing && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateOption}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                                Nouvelle option
                            </Button>
                        </Box>
                    )}

                    {loadingOptions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                    ) : options.length === 0 ? (
                        <Alert severity="info">Aucune option configurée pour cette application.</Alert>
                    ) : (
                        options.map((option) => (
                            <Accordion
                                key={option.id}
                                expanded={expandedOption === option.id}
                                onChange={(e, isExp) => setExpandedOption(isExp ? option.id : false)}
                                sx={{ mb: 1 }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                                        <OptionIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                        <Typography sx={{ fontWeight: 600 }}>{option.name}</Typography>
                                        <Chip label={option.key} size="small" variant="outlined" />
                                        <Chip label={option.is_active ? 'Active' : 'Inactive'} size="small"
                                            color={option.is_active ? 'success' : 'default'} />
                                        <Box sx={{ flexGrow: 1 }} />
                                        {canManagePricing && (
                                            <>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditOption(option); }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteOption(option); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {option.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {option.description}
                                        </Typography>
                                    )}
                                    <PricingPlansPanel
                                        accessToken={accessToken}
                                        priceableType="application_option"
                                        priceableId={option.id}
                                        applicationId={id}
                                        disabled={!canManagePricing}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))
                    )}
                </Box>
            )}

            {/* Dialog Option */}
            <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingOption ? 'Modifier l\'option' : 'Nouvelle option'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {optionFormError && <Grid item xs={12}><Alert severity="error">{optionFormError}</Alert></Grid>}
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Clé technique (ex: EXTRA_STORAGE)"
                                value={optionForm.key} onChange={(e) => setOptionForm(f => ({ ...f, key: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Nom" value={optionForm.name}
                                onChange={(e) => setOptionForm(f => ({ ...f, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline rows={2} label="Description"
                                value={optionForm.description} onChange={(e) => setOptionForm(f => ({ ...f, description: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={optionForm.is_active}
                                    onChange={(e) => setOptionForm(f => ({ ...f, is_active: e.target.checked }))} />}
                                label="Option active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOptionDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveOption} disabled={!optionForm.key || !optionForm.name}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingOption ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
